import {
  CampaignUserResultMessages,
  CampaignUserResultStatusesEnum,
  SenderProfileType,
} from '@/client/campaigns';
import {
  BranchStatusEnum,
  CourseStatusAttemptEnum,
  UserStatusEnum,
} from '@/client/courses';
import { SurveyDisplayStatusEnum } from '@/client/security-culture/types';
import { TranslationFunctionType } from '@/common/types';
import { AppChip } from '@/ui/chip';
import { t } from 'i18next';
import { uniqueId } from 'lodash';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import React, { ReactNode } from 'react';
import styled from 'styled-components';

export const determineAttemptStatusColor = (
  status: string,
  t: TranslationFunctionType,
) => {
  switch (status) {
    case CourseStatusAttemptEnum.IN_PROGRESS:
      return (
        <AppChip
          label={t('course.attempt.status.inProgress')}
          type="secondary"
          size="s"
        />
      );
    case CourseStatusAttemptEnum.COMPLETED:
      return (
        <AppChip
          label={t('course.attempt.status.completed')}
          type="primary"
          size="s"
        />
      );
    case CourseStatusAttemptEnum.FORCE_COMPLETED:
      return (
        <AppChip
          label={t('course.attempt.status.forceCompleted')}
          type="blue"
          size="s"
        />
      );
    case CourseStatusAttemptEnum.FAILED:
      return (
        <AppChip
          label={t('course.attempt.status.failed')}
          type="error"
          size="s"
        />
      );
    default:
      return (
        <AppChip
          label={t('course.attempt.status.enrolled')}
          type="secondary"
          size="s"
        />
      );
  }
};

export const determineScoreColor = (
  status: string,
  score: number | string | null,
) => {
  if (
    !score &&
    score !== 0 &&
    (status === CourseStatusAttemptEnum.COMPLETED ||
      status === CourseStatusAttemptEnum.FORCE_COMPLETED)
  ) {
    return (
      <AppChip
        label={t('content.noScore')}
        type={
          status === CourseStatusAttemptEnum.FORCE_COMPLETED
            ? 'blue'
            : 'primary'
        }
        size="s"
      />
    );
  }

  const scoreString = score ? score.toString() : '0';
  switch (status) {
    case CourseStatusAttemptEnum.IN_PROGRESS:
      return <AppChip label={scoreString} type="secondary" size="s" />;
    case CourseStatusAttemptEnum.COMPLETED:
      return <AppChip label={scoreString} type="primary" size="s" />;
    case CourseStatusAttemptEnum.FORCE_COMPLETED:
      return <AppChip label={scoreString} type="blue" size="s" />;
    case CourseStatusAttemptEnum.FAILED:
      return <AppChip label={scoreString} type="error" size="s" />;
    default:
      return <AppChip label={scoreString} type="secondary" size="s" />;
  }
};

export const determineUserStatusColor = (
  status: string,
  t: TranslationFunctionType,
) => {
  switch (status) {
    case UserStatusEnum.IN_PROGRESS:
      return (
        <AppChip
          label={t('course.attempt.status.inProgress')}
          type="secondary"
          size="s"
        />
      );
    case UserStatusEnum.COMPLETED:
      return (
        <AppChip
          label={t('course.attempt.status.completed')}
          type="primary"
          size="s"
        />
      );
    case UserStatusEnum.FORCE_COMPLETED:
      return (
        <AppChip
          label={t('course.attempt.status.forceCompleted')}
          type="blue"
          size="s"
        />
      );
    case UserStatusEnum.FAILED:
      return (
        <AppChip
          label={t('course.attempt.status.failed')}
          type="error"
          size="s"
        />
      );
    case UserStatusEnum.ENROLLED:
      return (
        <AppChip
          label={t('course.attempt.status.enrolled')}
          type="secondary"
          size="s"
        />
      );
    case UserStatusEnum.NOT_SCHEDULED:
      return (
        <AppChip
          label={t('course.attempt.status.notScheduled')}
          type="error"
          size="s"
        />
      );
    case UserStatusEnum.SCHEDULED:
      return (
        <AppChip
          label={t('course.attempt.status.scheduled')}
          type="secondary"
          size="s"
        />
      );
    default:
      return null;
  }
};

export const determineBranchStatusColor = (
  status: string,
  t: TranslationFunctionType,
) => {
  switch (status) {
    case BranchStatusEnum.NOT_SCHEDULED:
      return (
        <AppChip label={t('course.attempt.status.notScheduled')} type="error" />
      );
    case BranchStatusEnum.SCHEDULED:
      return (
        <AppChip
          label={t('course.attempt.status.scheduled')}
          type="secondary"
        />
      );
    case BranchStatusEnum.ENROLLED:
      return (
        <AppChip label={t('course.attempt.status.enrolled')} type="secondary" />
      );
    default:
      return null;
  }
};

export const isMobile = () => {
  const toMatch = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
  ];

  return toMatch.some((toMatchItem) => {
    return (
      navigator.userAgent.match(toMatchItem) ||
      'ontouchstart' in document.documentElement
    );
  });
};

export const difficultyOptions = [
  { label: '🐟', value: 1 },
  { label: '🐟🐟', value: 2 },
  { label: '🐟🐟🐟', value: 3 },
  { label: '🐟🐟🐟🐟', value: 4 },
  { label: '🐟🐟🐟🐟🐟', value: 5 },
];

export const difficultyFilterOptions = [
  { label: 1, value: 1 },
  { label: 2, value: 2 },
  { label: 3, value: 3 },
  { label: 4, value: 4 },
  { label: 5, value: 5 },
];

export const displayDifficulty = (campaignDifficulty: number): ReactNode => {
  const option = difficultyOptions.find((x) => x.value === campaignDifficulty);
  return option?.label;
};

export const senderOptions = (t: TranslationFunctionType) => [
  { label: t('generic.internal'), value: SenderProfileType.INTERNAL },
  { label: t('generic.external'), value: SenderProfileType.EXTERNAL },
];

const StyledItems = styled.span`
  font-style: italic;
  color: var(--gray-darker);
  cursor: pointer;
`;

export const displayMiltipleItems = (
  nameString: string,
  t: TranslationFunctionType,
) => {
  const nameArr = nameString.split(',');
  const showItemNames = nameArr.slice(0, 3);
  const showMoreItems = nameArr.slice(3);
  const id = uniqueId('items');

  return nameArr.length <= 3 ? (
    nameArr.join(', ')
  ) : (
    <>
      <span>{showItemNames.join(', ')}</span>
      <StyledItems id={id}>
        {` ${t('generic.items.more.count', {
          count: showMoreItems.length,
        })}`}
      </StyledItems>
      <Tooltip
        position="mouse"
        target={`#${id}`}
        content={showMoreItems.join(', ')}
      />
    </>
  );
};

export const parseFloatingNumber = (value?: number | string | null) => {
  if (value === null || value === undefined) {
    return 0;
  }

  const numericValue =
    typeof value === 'number' ? value : parseFloat(value.toString());

  if (Number.isNaN(numericValue) || !Number.isFinite(numericValue)) {
    return 0;
  }

  return parseFloat(numericValue.toFixed(2));
};

const ErrorTag = styled(Tag)`
  &.p-tag {
    background-color: var(--gray-darker);
  }
`;

export const getCampaignReportsStatusSeverity = (
  status: CampaignUserResultStatusesEnum | undefined,
  t: (key: string) => string,
  isText?: boolean,
) => {
  switch (status) {
    case CampaignUserResultStatusesEnum.CAMPAIGN_CREATED:
      return isText ? (
        t(
          CampaignUserResultMessages[
            CampaignUserResultStatusesEnum.CAMPAIGN_CREATED
          ],
        )
      ) : (
        <Tag
          value={t(
            CampaignUserResultMessages[
              CampaignUserResultStatusesEnum.CAMPAIGN_CREATED
            ],
          )}
          severity="success"
        />
      );
    case CampaignUserResultStatusesEnum.EMAIL_SENT:
      return isText ? (
        t(CampaignUserResultMessages[CampaignUserResultStatusesEnum.EMAIL_SENT])
      ) : (
        <Tag
          value={t(
            CampaignUserResultMessages[
              CampaignUserResultStatusesEnum.EMAIL_SENT
            ],
          )}
          severity="success"
        />
      );
    case CampaignUserResultStatusesEnum.EMAIL_OPENED:
      return isText ? (
        t(
          CampaignUserResultMessages[
            CampaignUserResultStatusesEnum.EMAIL_OPENED
          ],
        )
      ) : (
        <Tag
          value={t(
            CampaignUserResultMessages[
              CampaignUserResultStatusesEnum.EMAIL_OPENED
            ],
          )}
          severity="warning"
        />
      );
    case CampaignUserResultStatusesEnum.CLICKED_LINK:
      return isText ? (
        t(
          CampaignUserResultMessages[
            CampaignUserResultStatusesEnum.CLICKED_LINK
          ],
        )
      ) : (
        <Tag
          value={t(
            CampaignUserResultMessages[
              CampaignUserResultStatusesEnum.CLICKED_LINK
            ],
          )}
          className="orange"
        />
      );
    case CampaignUserResultStatusesEnum.SUBMITTED_DATA:
      return isText ? (
        t(
          CampaignUserResultMessages[
            CampaignUserResultStatusesEnum.SUBMITTED_DATA
          ],
        )
      ) : (
        <Tag
          value={t(
            CampaignUserResultMessages[
              CampaignUserResultStatusesEnum.SUBMITTED_DATA
            ],
          )}
          severity="danger"
        />
      );
    case CampaignUserResultStatusesEnum.EMAIL_REPORTED:
      return isText ? (
        t(
          CampaignUserResultMessages[
            CampaignUserResultStatusesEnum.EMAIL_REPORTED
          ],
        )
      ) : (
        <Tag
          value={t(
            CampaignUserResultMessages[
              CampaignUserResultStatusesEnum.EMAIL_REPORTED
            ],
          )}
          severity="success"
        />
      );
    case CampaignUserResultStatusesEnum.ERROR:
      return isText ? (
        t(CampaignUserResultMessages[CampaignUserResultStatusesEnum.ERROR])
      ) : (
        <ErrorTag
          value={t(
            CampaignUserResultMessages[CampaignUserResultStatusesEnum.ERROR],
          )}
          className="gray"
        />
      );
    case CampaignUserResultStatusesEnum.SCHEDULED:
      return isText ? (
        t(CampaignUserResultMessages[CampaignUserResultStatusesEnum.SCHEDULED])
      ) : (
        <Tag
          value={t(
            CampaignUserResultMessages[
              CampaignUserResultStatusesEnum.SCHEDULED
            ],
          )}
          severity="info"
        />
      );
    case CampaignUserResultStatusesEnum.SENDING:
      return isText ? (
        t(CampaignUserResultMessages[CampaignUserResultStatusesEnum.SENDING])
      ) : (
        <Tag
          value={t(
            CampaignUserResultMessages[CampaignUserResultStatusesEnum.SENDING],
          )}
          severity="info"
        />
      );

    default:
      return null;
  }
};

const StyledTag = styled(Tag)`
  &.p-tag-info {
    background-color: var(--gray-dark);
  }
  .p-tag-icon {
    margin-right: auto;
  }
`;

export const getRepotedStatus = (reported: boolean) =>
  reported ? (
    <StyledTag icon="pi pi-check-circle" severity="success" />
  ) : (
    <StyledTag icon="pi pi-times-circle" severity="info" />
  );

export const preventDropdownBrowserTranslate: {
  translate: 'yes' | 'no' | undefined;
  pt: {
    item: () => { translate: 'yes' | 'no' | undefined };
  };
} = {
  translate: 'no',
  pt: {
    item() {
      return { translate: 'no' };
    },
  },
};

export const getSurveyDisplayStatusChip = (
  displayStatus: SurveyDisplayStatusEnum | null | undefined,
  t: TranslationFunctionType,
): React.ReactNode => {
  switch (displayStatus) {
    case SurveyDisplayStatusEnum.SCHEDULED:
      return <AppChip label={t('generic.scheduled')} type="blue" />;
    case SurveyDisplayStatusEnum.ACTIVE:
      return <AppChip label={t('generic.active')} type="yellow" />;
    case SurveyDisplayStatusEnum.COMPLETED:
      return <AppChip label={t('generic.completed')} type="green" />;
    default:
      return <span>&#8212;</span>;
  }
};

export const getSecurityCultureChartWidth = (
  isMainDashboard: boolean,
  hasBenchmarkResult: boolean,
): string => {
  if (isMainDashboard) {
    return hasBenchmarkResult ? '70px' : '40px';
  }
  return hasBenchmarkResult ? '140px' : '70px';
};
